import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { deleteDraft } from '../offline/drafts';
import { useDeviceDrafts } from '../offline/useOffline';
import SyncIconButton from './SyncIconButton';

const SYNCED_ROW_MS = 4000;
const dateFormatter = new Intl.DateTimeFormat('en-GB', { dateStyle: 'short', timeStyle: 'short' });

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : dateFormatter.format(date);
}

// فحوصات الجهاز التي لم تُرفع، في أعلى قائمة الفحوصات بالشكل نفسه.
// بعد الرفع يبقى السطر لحظات بعلامة ✓ ثم يُحدَّث الجدول فيظهر الفحص بين فحوصات الخادم.
export default function DeviceDraftRows({ isViewer = false, onSynced }) {
  const drafts = useDeviceDrafts();
  const [syncedRows, setSyncedRows] = useState([]);
  const previousIds = useRef(new Set());
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // فحص رُفع من صفحة فحوصات الجهاز: نحدّث الجدول ليظهر بين فحوصات الخادم.
  useEffect(() => {
    const ids = new Set(drafts.map((draft) => draft.id));
    const vanished = [...previousIds.current].some((id) => !ids.has(id) && !syncedRows.some((row) => row.id === id));
    previousIds.current = ids;
    if (vanished) onSynced?.();
  }, [drafts, syncedRows, onSynced]);

  const markSynced = (draft, result) => {
    setSyncedRows((rows) => [...rows, { ...draft, code: result.code, server_id: result.server_id }]);
    timers.current.push(setTimeout(() => {
      setSyncedRows((rows) => rows.filter((row) => row.id !== draft.id));
      onSynced?.();
    }, SYNCED_ROW_MS));
  };

  const removeDraft = async (draft) => {
    if (!window.confirm(`حذف الفحص ${draft.code} من الجهاز نهائيًا؟ لم يُرفع إلى الخادم بعد.`)) return;
    await deleteDraft(draft.id);
  };

  const pending = drafts.filter((draft) => !syncedRows.some((row) => row.id === draft.id));

  return (
    <>
      {syncedRows.map((row) => (
        <tr key={`synced-${row.id}`} className="border-t bg-emerald-50/60">
          <td className="p-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700" title="تمت المزامنة">✓</span>
              <span dir="ltr">#{row.code}</span>
            </div>
          </td>
          <td className="p-3">{row.auction_name || '-'}</td>
          <td className="p-3">{row.owner_name || '-'}</td>
          <td className="p-3">{row.manufacturer || '-'}</td>
          <td className="p-3">{row.model || '-'}</td>
          <td className="p-3">
            <span className="inline-flex rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">تمت المزامنة</span>
          </td>
          <td className="p-3 whitespace-nowrap">{formatDate(row.created_at)}</td>
          <td className="p-3">
            <Link to={`/inspection/${row.server_id}/menu`} className="inline-flex items-center rounded-lg bg-primary px-3 py-1.5 text-white text-xs font-semibold hover:opacity-90">
              متابعة الفحص
            </Link>
          </td>
        </tr>
      ))}

      {pending.map((draft) => (
        <tr key={draft.id} className="border-t bg-amber-50/40">
          <td className="p-3">
            <div className="flex items-center gap-2">
              {!isViewer && (
                <SyncIconButton
                  draftId={draft.id}
                  lastError={draft.last_error || ''}
                  onSynced={(result) => markSynced(draft, result)}
                />
              )}
              <span dir="ltr">#{draft.code}</span>
            </div>
          </td>
          <td className="p-3">{draft.auction_name || '-'}</td>
          <td className="p-3">{draft.owner_name || '-'}</td>
          <td className="p-3">{draft.manufacturer || '-'}</td>
          <td className="p-3">{draft.model || '-'}</td>
          <td className="p-3">
            <span
              className="inline-flex rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800"
              title={`بانتظار الرفع: ${draft.pending_ops} عملية، منها ${draft.pending_photos} صورة`}
            >
              على الجهاز — {draft.status === 'completed' ? 'مكتمل' : 'قيد الإدخال'}
            </span>
          </td>
          <td className="p-3 whitespace-nowrap">{formatDate(draft.created_at)}</td>
          <td className="p-3">
            {!isViewer && (
              <div className="flex flex-wrap gap-2">
                <Link to={`/inspection/${draft.id}/menu`} className="inline-flex items-center rounded-lg bg-primary px-3 py-1.5 text-white text-xs font-semibold hover:opacity-90">
                  متابعة الفحص
                </Link>
                <Link to={`/inspection/${draft.id}/edit`} className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  تعديل
                </Link>
                <button
                  type="button"
                  onClick={() => removeDraft(draft)}
                  className="inline-flex items-center rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                >
                  حذف
                </button>
              </div>
            )}
          </td>
        </tr>
      ))}
    </>
  );
}
